import * as React from 'react';
const useResizeObserver = require('use-resize-observer/polyfilled');
import GoogleMapReact, { NESWBounds, ChildComponentProps, ChangeEventValue, Coords, Props as GoogleMapReactProps } from 'google-map-react';
import { fitBounds } from 'google-map-react';
import { LoadingSpinner } from '@cvent/carina/components/LoadingSpinner';

import { useDeepCompareMemoize } from '../search-result-page/hooks';
import { geopointValid, getBounds } from './utils';
import { MapContext } from './MapContext';
import { GMAPS_API_KEY } from '../../constants/config';
import AppLogger from '../../utils/logging';
const logger = AppLogger.createLogger();

const styles = require('./MapWithMarkers.scss');
const defaultCenter = { lat: 37.7749, lng: -122.4194 };
const defaultZoom = 9;

export interface MarkerProps extends Coords {
    id: string | number;
}

interface MapProps<M extends MarkerProps> {
    // Whether or not to show the loading state
    isLoading: boolean;

    // Marker geolocation and identifier data
    markerProps: M[];

    // Given props, render the marker
    renderMarker: (props: M, index: number) => React.ReactElement<ChildComponentProps>;

    // Event handler when venueMarker is clicked
    onMarkerClick?: (key: string, props: M) => void;

    // Overrides to underlying GoogleMapReact component
    googleMapsProps?: Partial<GoogleMapReactProps>;

    // Event handler that fires when the map component has loaded
    onGoogleMapLoad?: (map: google.maps.Map) => void;

    // Triggers when the map container loads, changes width, or the marker locations change
    shouldOverrideBounds?: (googleApiLoaded: boolean) => boolean;
    // Custom logic for handling bounds bounds recomputation, this runs before the default
    overrideBounds?: (markerProps: M[], googleApiLoaded: boolean) => NESWBounds | undefined;

    // Triggers when map is programatically updated to fit coordinates
    onMarkersFit?: (center: Coords, zoom: number, bounds: NESWBounds) => void;
}

/**
 * Common functionality:
 *
 * - Render map in a container
 * - Draw map after everything has loaded
 * - Loading state
 * - Render a set of geopoints with a specific marker
 * - Click handler for clicking marker
 * @param props
 */
const Map = <M extends MarkerProps>(props: MapProps<M>) => {
    const {
        markerProps,
        renderMarker,
        isLoading,
        googleMapsProps,
        onGoogleMapLoad,
        onMarkerClick,
        shouldOverrideBounds,
        overrideBounds,
        onMarkersFit,
    } = props;
    const cleanedMarkerProps = markerProps.filter(geopointValid);
    const _markerProps = useDeepCompareMemoize(cleanedMarkerProps);
    const _markerCoords = useDeepCompareMemoize(cleanedMarkerProps.map((m) => ({ id: m.id, lat: m.lat, lng: m.lng })));

    const { mapCenter, mapZoom, setMapCenter, setMapZoom } = React.useContext(MapContext);

    // State necessary to know when to draw the map
    const [googleApiLoaded, setGoogleApiLoaded] = React.useState(false);

    // We need to be able to react to map size changes
    const { ref, width: viewWidth = 0, height: viewHeight = 0 } = useResizeObserver();

    // Logic for dynamically computing map container size so we can compute
    // geo bounds and zoom level
    const handleOnLoad = (map: google.maps.Map) => {
        setGoogleApiLoaded(true);
        onGoogleMapLoad && onGoogleMapLoad(map);
    };
    // Handler for UI map changes
    const handleMapChange = (e: ChangeEventValue) => {
        setMapCenter(e.center);
        setMapZoom(e.zoom);
        logger.info(`Map Change: c${JSON.stringify(e.center)} z${e.zoom}`);
    };

    const mapReadyToMount = !!viewWidth && !!viewHeight && !!mapZoom && Boolean(mapCenter);
    const mapReadyToShow = mapReadyToMount && googleApiLoaded && !isLoading;

    let _shouldOverrideBounds = shouldOverrideBounds && shouldOverrideBounds(googleApiLoaded);
    let _overrideBounds: NESWBounds | undefined;
    if (_shouldOverrideBounds && !!overrideBounds) {
        _overrideBounds = overrideBounds(_markerProps, googleApiLoaded);
    }

    // Logic for computing map center and zoom level on initialization or search results change
    React.useEffect(() => {
        let bounds: NESWBounds | undefined;

        // If any of these conditions are true, there is either nothing to draw
        // or not container to draw on so we don't want to do any bounds computation
        if (_markerCoords.length === 0 || !viewWidth || !viewHeight) {
            return;
        }

        // Caller supplied bounds computation method
        if (_shouldOverrideBounds) {
            if (_overrideBounds) {
                bounds = _overrideBounds;
            }
        } else if (_markerCoords.length) {
            // Default bounds computation (fit to markers)
            bounds = getBounds(_markerCoords);
        }

        if (bounds) {
            const { center: fittedCenter, zoom: fittedZoom } = fitBounds(bounds, { width: viewWidth, height: viewHeight });

            setMapCenter(fittedCenter);
            setMapZoom(fittedZoom === -Infinity ? defaultZoom : fittedZoom);
            logger.info(
                `bounds: ${JSON.stringify(bounds)}; viewSize: ${viewWidth}x${viewHeight}; fittedCenter: ${JSON.stringify(
                    fittedCenter
                )}; fittedZoom: ${fittedZoom}; override?: ${_overrideBounds ? 'yes' : 'no'}`
            );
            onMarkersFit && onMarkersFit(fittedCenter, fittedZoom, bounds);
        }
    }, [_markerCoords, _shouldOverrideBounds, useDeepCompareMemoize(_overrideBounds), viewWidth, viewHeight, googleApiLoaded]);

    logger.info(`render: ${JSON.stringify(mapCenter)} center; ${mapZoom} zoom`);

    if (_markerProps.length === 0 && !isLoading) {
        return <div className={styles.loadingOverlay}></div>;
    }

    return (
        <div className={styles.container} ref={ref}>
            {!mapReadyToShow && (
                <div className={styles.loadingOverlay}>
                    <LoadingSpinner size='m' text='Loading...' />
                </div>
            )}
            {mapReadyToMount && (
                <GoogleMapReact
                    bootstrapURLKeys={{ key: GMAPS_API_KEY }}
                    onGoogleApiLoaded={({ map }) => handleOnLoad(map)}
                    onChange={handleMapChange}
                    onChildClick={onMarkerClick}
                    defaultCenter={defaultCenter}
                    center={mapCenter}
                    defaultZoom={defaultZoom}
                    zoom={mapZoom}
                    {...googleMapsProps}
                >
                    {_markerProps.map(renderMarker)}
                </GoogleMapReact>
            )}
        </div>
    );
};

export default Map;
